import { useRef, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable image upload with preview.
 * Uploads to POST /api/uploads/image → { url } and exposes the returned URL via onChange.
 * The URL is a backend-relative path (e.g. "/uploads/abc.jpg"); we normalize to absolute
 * so it works when embedded in <img src>.
 */
const ImageUpload = ({ value, onChange, label = "Upload image", accept = "image/*", aspect = "video", testId }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem("token");

  const absUrl = (u) => {
    if (!u) return "";
    if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("data:")) return u;
    return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/uploads/image`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(r.data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const clear = () => onChange("");

  const aspectClass = aspect === "square" ? "aspect-square" : aspect === "wide" ? "aspect-[16/6]" : "aspect-video";

  return (
    <div className="space-y-2" data-testid={testId}>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleFile} data-testid={testId ? `${testId}-file` : undefined} />
      {value ? (
        <div className="relative group">
          <div className={`${aspectClass} w-full rounded-lg overflow-hidden border bg-slate-50`}>
            <img src={absUrl(value)} alt="preview" className="w-full h-full object-cover" />
          </div>
          <div className="absolute top-2 right-2 flex gap-2">
            <Button type="button" size="sm" variant="secondary" onClick={() => inputRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span className="ml-1">Replace</span>
            </Button>
            <Button type="button" size="sm" variant="destructive" onClick={clear}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className={`${aspectClass} w-full rounded-lg border-2 border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 transition-colors flex flex-col items-center justify-center text-slate-500 gap-2`}
          data-testid={testId ? `${testId}-drop` : undefined}
        >
          {uploading ? (
            <>
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Uploading…</span>
            </>
          ) : (
            <>
              <ImageIcon className="h-6 w-6" />
              <span className="text-sm font-medium">{label}</span>
              <span className="text-xs">JPG, PNG, WebP · Max 8 MB</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default ImageUpload;
